/**
 * Toast notification utility.
 *
 * v3: delegates to Sonner (https://sonner.emilkowal.ski/), the standard toast
 * library for this app. Sonner's `<Toaster />` is mounted at the root via
 * `NotificationProvider` in `app/layout.tsx`, so any module — component,
 * Redux thunk, service — can call `toast.success(...)` and the themed,
 * icon-ed toast appears in the user-configured position.
 *
 * History:
 *   v1 — DOM-based custom renderer with inline styles.
 *   v2 — delegated to Shadcn/Radix toaster.
 *   v3 — delegated to Sonner for app-wide consistency with the notification
 *        feature's existing Sonner usage.
 *
 * The public API (`toast.success`, `toast.error`, `toast.warning`,
 * `toast.info`) is unchanged, so existing consumers don't need edits.
 */

import { toast as sonnerToast } from 'sonner';
import type { ToastPosition } from '@/app/types/ui/toast';

interface ToastOptions {
  duration?: number;
  position?: ToastPosition;
  /** Optional header shown above the description. */
  title?: string;
  /** Additional detail text beneath the main message. */
  description?: string;
}

function buildSonnerOptions(message: string, options: ToastOptions = {}) {
  // When a caller passes a title, promote `message` to description so the
  // title renders bold above and the message wraps below. Otherwise pass
  // the message directly and let sonner render it as the primary line.
  return {
    duration: options.duration,
    position: options.position,
    description: options.title ? message : options.description,
  };
}

class ToastManager {
  success(message: string, options: ToastOptions = {}) {
    if (typeof window === 'undefined') return;
    const opts = buildSonnerOptions(message, options);
    sonnerToast.success(options.title ?? message, opts);
  }

  error(message: string, options: ToastOptions = {}) {
    if (typeof window === 'undefined') return;
    const opts = buildSonnerOptions(message, options);
    sonnerToast.error(options.title ?? message, opts);
  }

  warning(message: string, options: ToastOptions = {}) {
    if (typeof window === 'undefined') return;
    const opts = buildSonnerOptions(message, options);
    sonnerToast.warning(options.title ?? message, opts);
  }

  info(message: string, options: ToastOptions = {}) {
    if (typeof window === 'undefined') return;
    const opts = buildSonnerOptions(message, options);
    sonnerToast.info(options.title ?? message, opts);
  }
}

export const toast = new ToastManager();
